/**
 * The languages the app can be shown in, and how one is chosen before there is an
 * account to read it from.
 *
 * The language is a preference on the user (`users.language`), like the units
 * (DECISIONS.md DS-5, DS-6): after login the server's copy wins. Before login —
 * landing, signup, login, password reset — it comes from the device: the owner's
 * pick on the language switch ("PT | ES | EN"), kept in localStorage, else the
 * browser locale (any pt* locale is pt-BR, any es* locale es, anything else
 * English). Signup sends that choice up, and the account carries it from then on.
 *
 * A language is OFFERED only once its resource file is complete (i18n.ts,
 * lib/localeCompleteness.ts). Until then the switch, the signup field and the
 * Settings control do not render, and a stored choice of it reads as English.
 *
 * This module does not import i18n, so i18n.ts can use it while initialising.
 */

export type Language = 'en' | 'pt-BR' | 'es';

/**
 * Every language a user can hold. Keep in sync with the backend (schemas/auth.py).
 * Spanish is one language for Spain and Latin America; where the country matters
 * (lab units, support services) it comes from the browser (lib/region.ts).
 */
export const SUPPORTED_LANGUAGES: readonly Language[] = ['en', 'pt-BR', 'es'];

export const DEFAULT_LANGUAGE: Language = 'en';

export function isLanguage(value: unknown): value is Language {
  return typeof value === 'string' && (SUPPORTED_LANGUAGES as readonly string[]).includes(value);
}

/**
 * The language for a browser locale: any Portuguese locale is pt-BR, any Spanish
 * locale es, the rest English.
 */
export function languageForLocale(locale: string | null | undefined): Language {
  if (locale && /^pt(-|_|$)/i.test(locale)) return 'pt-BR';
  if (locale && /^es(-|_|$)/i.test(locale)) return 'es';
  return 'en';
}

/** The localStorage key for the pre-login choice. */
export const PRE_ACCOUNT_LANGUAGE_KEY = 'language';

/** The owner's pick on the pre-login switch, if they made one. */
export function storedPreAccountLanguage(): Language | null {
  try {
    const stored = localStorage.getItem(PRE_ACCOUNT_LANGUAGE_KEY);
    return isLanguage(stored) ? stored : null;
  } catch {
    return null;
  }
}

export function storePreAccountLanguage(lang: Language): void {
  try {
    localStorage.setItem(PRE_ACCOUNT_LANGUAGE_KEY, lang);
  } catch {
    // localStorage unavailable — the choice lasts for this page only.
  }
}

/** The language a signed-in session last used, from the cached user. */
export function cachedUserLanguage(): Language | null {
  try {
    const raw = localStorage.getItem('user');
    if (!raw || !localStorage.getItem('authToken')) return null;
    const language = (JSON.parse(raw) as { language?: unknown }).language;
    return isLanguage(language) ? language : DEFAULT_LANGUAGE;
  } catch {
    return null;
  }
}

/** The short code a switch shows: "EN", "PT", "ES". */
export function languageCode(lang: Language): string {
  return lang.split('-')[0].toUpperCase();
}

/**
 * The language's own name for itself ("English"), from the browser's locale data,
 * so no language's name has to be written into another's resource file.
 */
export function languageEndonym(lang: Language): string {
  try {
    const name = new Intl.DisplayNames([lang], { type: 'language' }).of(lang);
    if (name) return name.charAt(0).toLocaleUpperCase(lang) + name.slice(1);
  } catch {
    // Intl.DisplayNames unavailable — fall through to the code.
  }
  return languageCode(lang);
}
